import React from 'react';

const fieldErrorStyle = { color: '#ef4444', fontSize: '0.875rem', marginTop: '4px' };

const SectionTitle = ({ variant, children }) =>
  variant === 'public' ? (
    <h3 className="visa-enquiry-section-title">{children}</h3>
  ) : (
    <h2 className="section-title">{children}</h2>
  );

const FormSection = ({ variant, title, children }) =>
  variant === 'public' ? (
    <>
      <SectionTitle variant={variant}>{title}</SectionTitle>
      <div className="form-grid">{children}</div>
    </>
  ) : (
    <div className="form-section">
      <SectionTitle variant={variant}>{title}</SectionTitle>
      <div className="row">{children}</div>
    </div>
  );

const FieldWrap = ({ variant, col = 6, fullWidth, children }) =>
  variant === 'public' ? (
    <div className={`form-group${fullWidth ? ' full-width' : ''}`}>{children}</div>
  ) : (
    <div className={`${fullWidth ? 'col-12' : `col-md-${col}`} mb-3`}>{children}</div>
  );

const FieldLabel = ({ variant, htmlFor, required, children }) =>
  variant === 'public' ? (
    <label htmlFor={htmlFor}>{children}{required ? ' *' : ''}</label>
  ) : (
    <label className="form-label" htmlFor={htmlFor}>
      {children}
      {required && <span className="required">*</span>}
    </label>
  );

const inputClass = (variant, hasError) =>
  variant === 'public' ? undefined : `input-field${hasError ? ' error' : ''}`;

export default function VisaEnquiryFormFields({
  data,
  onChange,
  fieldErrors = {},
  disabled = false,
  countries = [],
  variant = 'admin',
}) {
  const err = (name) => fieldErrors[name];
  const inputStyle = (name) => (variant === 'public' && err(name) ? { borderColor: '#ef4444' } : {});

  const textField = (name, label, { required = false, type = 'text', col = 6, fullWidth = false } = {}) => (
    <FieldWrap variant={variant} col={col} fullWidth={fullWidth}>
      <FieldLabel variant={variant} htmlFor={name} required={required}>{label}</FieldLabel>
      <input
        type={type}
        id={name}
        name={name}
        className={inputClass(variant, err(name))}
        value={data[name] || ''}
        onChange={onChange}
        required={required}
        disabled={disabled}
        style={inputStyle(name)}
      />
      {err(name) && <span style={fieldErrorStyle}>{err(name)}</span>}
    </FieldWrap>
  );

  const textAreaField = (name, label, { fullWidth = true, rows = 2 } = {}) => (
    <FieldWrap variant={variant} fullWidth={fullWidth}>
      <FieldLabel variant={variant} htmlFor={name}>{label}</FieldLabel>
      <textarea
        id={name}
        name={name}
        rows={rows}
        className={inputClass(variant, err(name))}
        value={data[name] || ''}
        onChange={onChange}
        disabled={disabled}
        style={inputStyle(name)}
      />
    </FieldWrap>
  );

  const radioField = (name, label, options) => (
    <FieldWrap variant={variant} col={6}>
      <FieldLabel variant={variant}>{label}</FieldLabel>
      <div className="visa-enquiry-radios" role="radiogroup" aria-label={label}>
        {options.map((opt) => (
          <label key={opt} className="visa-enquiry-radio-option">
            <input
              type="radio"
              name={name}
              value={opt}
              checked={data[name] === opt}
              onChange={onChange}
              disabled={disabled}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </FieldWrap>
  );

  return (
    <div className={variant === 'admin' ? 'visa-enquiry-form' : undefined}>
      <FormSection variant={variant} title="Applicant's Information">
        {textField('firstName', 'First Name (as per passport)', { required: true, col: 4 })}
        {textField('middleName', 'Middle Name', { col: 4 })}
        {textField('lastName', 'Last Name', { required: true, col: 4 })}
        {textField('dateOfBirth', 'Date of Birth (D/M/Y)', { type: 'date', col: 4 })}
        {textField('nationality', 'Nationality', { col: 4 })}
        {textField('cityCountryOfBirth', 'City & Country of Birth', { col: 4 })}
        {textField('passportNo', 'Passport No.', { col: 4 })}
        {textField('passportDateOfIssue', 'Date of Issue', { type: 'date', col: 4 })}
        {textField('passportPlaceOfIssue', 'Place of Issue', { col: 4 })}
        {radioField('gender', 'Gender', ['Male', 'Female'])}
        {radioField('maritalStatus', 'Marital Status', ['Single', 'Married', 'Divorced', 'Widowed'])}
        {textAreaField('residentialAddress', 'Current Residential Address for Correspondence')}
        {textField('postalCode', 'Postal Code', { col: 3 })}
        {textField('mobileNo', 'Mobile No.', { required: true, col: 3 })}
        {textField('landlineNo', 'Landline No.', { col: 3 })}
        {textField('email', 'Email', { required: true, type: 'email', col: 3 })}
        {textField('alternativeEmail', 'Alternative Email', { type: 'email', col: 6 })}
        {textField('occupation', 'Occupation', { col: 12, fullWidth: true })}
      </FormSection>

      <FormSection variant={variant} title="Emergency Contact Details">
        {textField('emergencyFullName', 'Full Name', { col: 6 })}
        {textField('emergencyRelation', 'Relation', { col: 6 })}
        {textField('emergencyContactNo', 'Contact No.', { col: 6 })}
        {textField('emergencyEmail', 'Email', { type: 'email', col: 6 })}
        {textAreaField('emergencyAddress', 'Current Residential Address')}
        {textField('emergencyPostalCode', 'Postal Code', { col: 6 })}
      </FormSection>

      <FormSection variant={variant} title="Sponsor Contact Details">
        {textField('sponsorFullName', 'Full Name', { col: 6 })}
        {textField('sponsorRelation', 'Relation', { col: 6 })}
        {textField('sponsorContactNo', 'Contact No.', { col: 6 })}
        {textField('sponsorEmail', 'Email', { type: 'email', col: 6 })}
        {textField('sponsorProfession', 'Profession', { col: 6 })}
        {textAreaField('sponsorAddress', 'Current Residential Address')}
        {textField('sponsorPostalCode', 'Postal Code', { col: 6 })}
      </FormSection>

      <FormSection variant={variant} title="Country of Intended Travel">
        <FieldWrap variant={variant} col={6}>
          <FieldLabel variant={variant} htmlFor="intendedCountry">Country</FieldLabel>
          {countries.length > 0 ? (
            <select
              id="intendedCountry"
              name="intendedCountry"
              className={inputClass(variant, false)}
              value={data.intendedCountry || ''}
              onChange={onChange}
              disabled={disabled}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.id} value={c.country_name}>{c.country_name}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="intendedCountry"
              name="intendedCountry"
              className={inputClass(variant, false)}
              value={data.intendedCountry || ''}
              onChange={onChange}
              disabled={disabled}
            />
          )}
        </FieldWrap>
        {textField('purposeOfVisit', 'Purpose of Visit', { col: 6 })}
        {textField('travelMonthYear', 'Month & Year', { type: 'month', col: 6 })}
      </FormSection>
    </div>
  );
}
